<script setup>
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import RadioButton from "primevue/radiobutton";
import {ref} from "vue";

const emits = defineEmits(["next"]);
const props = defineProps([
    "form",
    'errors',
    'v$',
    'thyroidGlandOptions',
]);
const value = ref(10);

const medicines = ref([
    "Kortison",
    "Blutverdünnende Medikamente",
    "Medikamente gegen Bluthochdruck",
    "Migräne-Medikamente",
    "Psychopharmaka",
    "Insulin",
    "Antihistaminika",
    "Immunsuppressiva",
    "Antibiotika",
]);
const yesNo = ref([
    {
        label: "ja",
        value: true,
    },
    {
        label: "nein",
        value: false,
    }
]);
</script>
<template>
    <div class="lato p-7 rounded my-2">
        <div class="text-xl font-semibold mb-5">Medikamente</div>

        <div class="my-7">
            <div class="font-bold">
                Welche Medikamente nimmst du zur Zeit ein?
            </div>

            <div class="grid grid-cols-2 gap-4 my-4">
                <div class="" v-for="(item, i) in medicines" :key="i">
                    <div class="flex items-center gap-4">
                        <Checkbox
                            :disabled="form.is_no_medications == true"
                            v-model="form.medications"
                            :value="item" />

                        <div class="">{{ item }}</div>
                    </div>
                </div>

                <div class="col-span-2">
                    <div class="flex items-center gap-4">
                        <Checkbox
                            :disabled="form.medications.length"
                            :invalid="v$.form.is_no_medications.$error"
                            binary="true"
                            v-model="form.is_no_medications"
                            @change="v$.form.is_no_medications.$touch"
                        />

                        <div class="">Zur Zeit nehme ich keine Medikamente ein</div>
                    </div>

                    <div v-if="v$.form.is_no_medications.$error" class="text-sm text-[#dc2626]">
                        {{ v$.form.is_no_medications.$errors[0].$message }}
                    </div>
                </div>
            </div>
        </div>

        <div class="my-7">
            <div class="text-xl font-semibold mb-5">Schilddrüse</div>

            <div class="grid grid-cols-1 gap-4">
                <div class="" v-for="(item, i) in thyroidGlandOptions" :key="i">
                    <div class="flex items-center gap-4">
                        <RadioButton
                            v-model="form.thyroid_gland"
                            :value="item.value" />

                        <div class="">{{ item.label }}</div>
                    </div>
                </div>

                <div class="grid grid-cols-2">
                    <div class="flex items-center gap-4">
                        <span>Andere</span>

                        <InputText v-model="form.thyroid_gland_other" class="w-full"></InputText>
                    </div>
                </div>
            </div>
        </div>

        <div class="my-7">
            <div class="text-xl font-semibold mb-5">Injektionen</div>

            <div class="mb-4">
                <div class="font-bold my-3">Wurde bei dir in den letzten 6 Monaten Botox gespritzt?</div>

                <div class="grid grid-cols-2">
                    <div class="flex items-center gap-5">
                        <div class="flex items-center gap-4">
                            <RadioButton
                                :invalid="v$.form.botox_last_6_months.$error"
                                v-model="form.botox_last_6_months"
                                :value="true"
                                @change="v$.form.botox_last_6_months.$touch"
                            />

                            <div>ja</div>
                        </div>

                        <div>
                            <InputText
                                :invalid="v$.form.botox_details.$error"
                                v-model="form.botox_details"
                                type="text"
                                class="w-full"
                                placeholder="wo"
                                @input="v$.form.botox_details.$touch"
                                @blur="v$.form.botox_details.$touch"
                            />

                            <div v-if="v$.form.botox_details.$error" class="text-sm text-[#dc2626]">
                                {{ v$.form.botox_details.$errors[0].$message }}
                            </div>
                        </div>

                    </div>

                    <div class="flex items-center gap-4">
                        <RadioButton
                            :invalid="v$.form.botox_last_6_months.$error"
                            v-model="form.botox_last_6_months"
                            :value="false"
                            @change="v$.form.botox_last_6_months.$touch"
                        />

                        <div>nein</div>
                    </div>

                    <div v-if="v$.form.botox_last_6_months.$error" class="text-sm text-[#dc2626]">
                        {{ v$.form.botox_last_6_months.$errors[0].$message }}
                    </div>
                </div>
            </div>

            <div class="mb-4">
                <div class="font-bold my-3">Hast du in den letzten 6 Monaten Filler-Behandlungen erhalten?</div>

                <div class="grid grid-cols-2">
                    <div class="flex items-center gap-5">
                        <div class="flex items-center gap-4">
                            <RadioButton
                                :invalid="v$.form.filler_treatment_last_6_months.$error"
                                v-model="form.filler_treatment_last_6_months"
                                :value="true"
                                @change="v$.form.filler_treatment_last_6_months.$touch"
                            />

                            <div>ja</div>
                        </div>

                        <div>
                            <InputText
                                :invalid="v$.form.filler_treatment_details.$error"
                                v-model="form.filler_treatment_details"
                                type="text"
                                class="w-full"
                                placeholder="wo"
                                @input="v$.form.filler_treatment_details.$touch"
                                @blur="v$.form.filler_treatment_details.$touch"
                            />

                            <div v-if="v$.form.filler_treatment_details.$error" class="text-sm text-[#dc2626]">
                                {{ v$.form.filler_treatment_details.$errors[0].$message }}
                            </div>
                        </div>
                    </div>

                    <div class="flex items-center gap-4">
                        <RadioButton
                            :invalid="v$.form.filler_treatment_last_6_months.$error"
                            v-model="form.filler_treatment_last_6_months"
                            :value="false"
                            @change="v$.form.filler_treatment_last_6_months.$touch"
                        />

                        <div>nein</div>
                    </div>

                    <div v-if="v$.form.filler_treatment_last_6_months.$error" class="text-sm text-[#dc2626]">
                        {{ v$.form.filler_treatment_last_6_months.$errors[0].$message }}
                    </div>
                </div>
            </div>
        </div>

        <div class="my-7">
            <div class="text-xl font-semibold mb-5">Implantate</div>

            <div class="grid grid-cols-2 gap-4">
                <div>
                    <div class="flex items-center gap-4">
                        <RadioButton
                            :invalid="v$.form.is_implants.$error"
                            v-model="form.is_implants"
                            :value="true"
                            @change="v$.form.is_implants.$touch"
                        />

                        <div>ja</div>
                    </div>

                    <div v-if="v$.form.is_implants.$error" class="text-sm text-[#dc2626]">
                        {{ v$.form.is_implants.$errors[0].$message }}
                    </div>
                </div>

                <div class="flex items-center gap-4">
                    <RadioButton
                        :invalid="v$.form.is_implants.$error"
                        v-model="form.is_implants"
                        :value="false"
                        @change="v$.form.is_implants.$touch"
                    />

                    <div>nein</div>
                </div>

                <div class="flex items-center gap-5">
                    <div>
                        <div class="flex items-center gap-4">
                            <Checkbox
                                :invalid="v$.form.silicone.$error"
                                v-model="form.silicone"
                                :binary="true"
                                @input="v$.form.silicone.$touch" />

                            <div class="">
                                {{ $t("Silicone") }}
                            </div>
                        </div>
                    </div>

                    <div>
                        <InputText
                            :invalid="v$.form.silicone_where.$error"
                            v-model="form.silicone_where"
                            type="text"
                            class="w-full"
                            placeholder="wo"
                            @input="v$.form.silicone_where.$touch"
                            @blur="v$.form.silicone_where.$touch"
                        />

                        <div v-if="v$.form.silicone_where.$error" class="text-sm text-[#dc2626]">
                            {{ v$.form.silicone_where.$errors[0].$message }}
                        </div>
                    </div>
                </div>

                <div class="flex items-center gap-5">
                    <div>
                        <div class="flex items-center gap-4">
                            <Checkbox
                                :invalid="v$.form.metal.$error"
                                v-model="form.metal"
                                :binary="true"
                                @blur="v$.form.metal.$touch" />

                            <div class="">
                                {{ $t("Metal") }}
                            </div>
                        </div>
                    </div>

                    <div>
                        <InputText
                            :invalid="v$.form.metal_where.$error"
                            v-model="form.metal_where"
                            type="text"
                            class="w-full"
                            placeholder="wo"
                            @input="v$.form.metal_where.$touch"
                            @blur="v$.form.metal_where.$touch"
                        />

                        <div v-if="v$.form.metal_where.$error" class="text-sm text-[#dc2626]">
                            {{ v$.form.metal_where.$errors[0].$message }}
                        </div>
                    </div>
                </div>

                <div v-if="v$.form.silicone.$error || v$.form.metal.$error" class="text-sm text-[#dc2626]">
                    {{ v$.form.silicone.$errors[0]?.$message ?? v$.form.metal.$errors[0]?.$messages }}
                </div>
            </div>
        </div>

        <div class="my-7">
            <div class="font-bold">Implantierte aktive Systeme wie</div>
            <div class="grid grid-cols-2 gap-4 my-4">
                <div>
                    <div class="flex items-center gap-4">
                        <RadioButton
                            :invalid="v$.form.is_active_systems.$error"
                            v-model="form.is_active_systems"
                            :value="true"
                            @change="v$.form.is_active_systems.$touch"
                        />

                        <div>ja</div>
                    </div>

                    <div v-if="v$.form.is_active_systems.$error" class="text-sm text-[#dc2626]">
                        {{ v$.form.is_active_systems.$errors[0].$message }}
                    </div>
                </div>

                <div>
                    <div class="flex items-center gap-4">
                        <RadioButton
                            :invalid="v$.form.is_active_systems.$error"
                            v-model="form.is_active_systems"
                            :value="false"
                            @change="v$.form.is_active_systems.$touch"
                        />

                        <div>nein</div>
                    </div>
                </div>
            </div>

            <div class="my-4">
                <div class="grid grid-cols-1 gap-4">
                    <div
                        class=""
                        v-for="(item, i) in [
                            'Spritzensysteme, Dialysesystem, Neurostimulatoren o.ä.',
                            'Herzschrittmacher, ICD',
                        ]"
                        :key="i"
                    >
                        <div class="flex items-center gap-4">
                            <Checkbox
                                :invalid="v$.form.implanted_active.$error"
                                v-model="form.implanted_active"
                                :value="item"
                                @change="v$.form.implanted_active.$touch"
                            />

                            <div class="">{{ item }}</div>
                        </div>
                    </div>
                </div>

                <div v-if="v$.form.implanted_active.$error" class="text-sm text-[#dc2626]">
                    {{ v$.form.implanted_active.$errors[0].$message }}
                </div>
            </div>
        </div>

        <div class="flex mt-10 justify-end">
            <Button
                label="weiter"
                class="min-w-[100px] !bg-black !text-[#FFF053] !border-none"
                @click="emits('next', 'save', 'medicines')"
            />
        </div>
    </div>
</template>
